import * as React from "react";
import { useRef, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { CalendarIcon } from "@phosphor-icons/react/dist/ssr/Calendar";
import dayjs from "dayjs";
import { Dayjs } from "dayjs";

interface Props {
	label: string;
	value: string | null;
	setValue: (value: string | null) => void;
	placeholder?: string;
	fieldStyles?: any;
	error?: boolean;
	minDate?: Dayjs;
}

export function CustomDatePicker({
	label,
	value,
	setValue,
	placeholder = "Select a date",
	fieldStyles = {},
	error = false,
  minDate,
}: Props) {
	const [isOpen, setIsOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);

	const handleOpen = () => setIsOpen(true);
	const handleClose = () => setIsOpen(false);

	const handleDateSelect = (date: string | null) => {
		setValue(date);
	};

	const valueDate = value ? dayjs(value) : null;

	return (
		<Box sx={{ ...fieldStyles, width: "100%" }}>
			<Box
				ref={anchorRef}
				component="button"
				type="button"
				onClick={handleOpen}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						handleOpen();
					}
				}}
				tabIndex={0}
				aria-label={label}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				sx={{
					...fieldStyles,
					display: "flex",
					alignItems: "center",
					gap: 1,
					width: "100%",
					backgroundColor: "transparent",
					p: 0,
					mt: 0,
					border: "none",
					outline: "none",
					boxShadow: "none",
					cursor: "pointer",
					// Keyboard focus ring and input border highlight
          '& .MuiInputLabel-root': {
            alignSelf: 'flex-start !important',
          },
					"&:focus-visible .MuiInputBase-root": {
						borderColor: "var(--mui-palette-primary-main)",
						boxShadow: "0 0 0 1px var(--mui-palette-primary-main)",
					},
					"&:focus-visible .MuiInputLabel-root": {
						color: "primary.main",
					},
					// Keep focus styles when DatePicker is open
					"&[aria-expanded='true'] .MuiInputBase-root": {
						borderColor: "var(--mui-palette-primary-main)",
						boxShadow: "0 0 0 1px var(--mui-palette-primary-main)",
					},
					"&[aria-expanded='true'] .MuiInputLabel-root": {
						color: "primary.main",
					},
				}}
			>
				<TextField
					variant="outlined"
					label={label}
					placeholder={placeholder}
					value={valueDate ? valueDate.format("DD/MM/YYYY") : ""}
					InputProps={{
						readOnly: true,
						error: error,
						endAdornment: (
							<Typography
								variant="subtitle2"
								color="text.secondary"
								style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 1 }}
							>
								<CalendarIcon  style={{ cursor: "pointer" }} width={24} height={24} weight="fill" />
							</Typography>
						),
					}}
					inputProps={{
						tabIndex: -1,
					}}
					sx={{
						flex: "1",
            mt: label ? 0 : 1,
						justifyContent: "flex-start",
						textTransform: "none",
						cursor: "pointer",
						"& .MuiInputBase-input": {
							cursor: "pointer",
							fontSize: "16px",
						},
						// Label focus styles
						"& .MuiInputLabel-root": {
							"&.Mui-focused": {
								color: "primary.main",
							},
						},
						// Input focus styles
						"& .MuiOutlinedInput-root": {
              '&:before': {
                content: 'none',
              },
							"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
								borderColor: "primary.main",
							},
						},
						...fieldStyles,
					}}
					focused={isOpen}
				/>
			</Box>

			<DatePicker
				open={isOpen}
				onClose={handleClose}
				value={valueDate}
        minDate={minDate}
				onChange={(date) => handleDateSelect(date ? date.format("YYYY-MM-DD") : null)}
				slotProps={{
					textField: { sx: { display: "none" } },
					popper: {
						anchorEl: anchorRef.current,
						placement: "bottom-start",
						sx: {
							"& .MuiPaper-root": {
								boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
								borderRadius: 2,
							},
						},
					},
          actionBar: {
						actions: ['clear', 'cancel', 'today', 'accept'],
					},
				}}
				views={["year", "month", "day"]}
				closeOnSelect
			/>
		</Box>
	);
}
